import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  useLLMConfig,
  useLLMModels,
  usePatchLLMConfig,
  type LLMConfig,
  type LLMModel,
} from "@/api/admin/llm-config";
import { ApiError } from "@/api/client";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { ConfirmModal } from "@/components/admin/ConfirmModal";

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const H1: CSSProperties = { fontFamily: "var(--font-display)", fontSize: 22 };
const GRID: CSSProperties = { display: "grid", gap: 12, gridTemplateColumns: "1fr" };
const CARD: CSSProperties = {
  background: "var(--d-ink-2)",
  borderRadius: 22,
  padding: 20,
  border: "1px solid var(--d-line)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const ROW: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};
const LABEL: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 12,
  color: "var(--d-fg-muted)",
};
const VALUE: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  color: "var(--d-fg)",
};
const INPUT: CSSProperties = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-mono)",
};
const BTN: CSSProperties = {
  padding: "8px 16px",
  borderRadius: 999,
  border: "none",
  background: "var(--d-violet)",
  color: "#fff",
  cursor: "pointer",
};

function estimateUSD(caps: LLMModel | undefined, maxTokens: number): number {
  if (!caps) return 0;
  // Catalogue prices are per 1M tokens; this works in thousands.
  // Assume ~500 input tokens average across prompts; max_tokens caps output.
  return (caps.price_in_per_1m / 1000) * 0.5 + (caps.price_out_per_1m / 1000) * (maxTokens / 1000);
}

/** The 422s from `PATCH /api/admin/llm-config/{op}` carry a `detail.message`
 * naming the rejected (model, reasoning_effort) combination. Without this
 * the status line would read "API error 422" and throw the explanation away. */
function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: { message?: unknown } } | null)?.detail;
    if (typeof detail?.message === "string") return detail.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export function LLMConfigPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading, isError, refetch } = useLLMConfig();
  const models = useLLMModels();
  const patch = usePatchLLMConfig();
  const [drafts, setDrafts] = useState<Record<string, Partial<LLMConfig>>>({});
  const [pendingOp, setPendingOp] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  function draftFor(c: LLMConfig): LLMConfig {
    return { ...c, ...drafts[c.operation] };
  }
  function setDraft(op: string, fragment: Partial<LLMConfig>): void {
    setDrafts((d) => ({ ...d, [op]: { ...d[op], ...fragment } }));
  }
  function save(op: string): void {
    const fragment = drafts[op];
    if (!fragment) return;
    // The backend PATCH treats a missing key as "leave as-is" and can't clear
    // `temperature` to null (see `LLMConfigRepository.update`), so an emptied
    // field must be omitted from the body rather than sent as `null`
    // — that's what "non-empty" means here.
    // `reasoning_effort` (DUE-279) has no such limit: an explicit `null`
    // clears it back to the model family's default, so the draft's value
    // goes out as it is — string or null.
    const { temperature, ...rest } = fragment;
    const patchBody: Parameters<typeof patch.mutate>[0]["patch"] = { ...rest };
    if (typeof temperature === "number") {
      patchBody.temperature = temperature;
    }
    patch.mutate(
      { operation: op, patch: patchBody },
      {
        onSuccess: () => {
          setStatus(`${op} updated`);
          setDrafts((d) => {
            const c = { ...d };
            delete c[op];
            return c;
          });
          setPendingOp(null);
        },
        onError: (e) => {
          setStatus(errorMessage(e));
          setPendingOp(null);
        },
      },
    );
  }

  if (isError || models.isError) {
    return (
      <AdminErrorState
        onRetry={() => {
          void refetch();
          void models.refetch();
        }}
      />
    );
  }
  if (isLoading || models.isLoading) return <div style={PAGE}>{t("common.loading")}</div>;

  const catalogue = models.data ?? [];
  // Mirrors `resolve_model` on the backend: a dated snapshot
  // (`gpt-5-mini-2025-08-07`) is its family; anything else is unknown.
  const capsOf = (model: string): LLMModel | undefined => {
    const family = model.replace(/-\d{4}-\d{2}-\d{2}$/, "");
    return catalogue.find((m) => m.name === family);
  };

  return (
    <div style={PAGE}>
      <h2 style={H1}>{t("llm_config.title")}</h2>
      {status !== null && (
        <div role="status" style={LABEL}>
          {status}
        </div>
      )}
      <div style={GRID}>
        {(data ?? []).map((row) => {
          const v = draftFor(row);
          const dirty = drafts[row.operation] !== undefined;
          const caps = capsOf(v.model);
          return (
            <div key={row.operation} style={CARD}>
              <div style={ROW}>
                <div style={{ ...H1, fontSize: 18 }}>{row.operation}</div>
                <label>
                  <span style={LABEL}>{t("llm_config.enabled")}</span>{" "}
                  <input
                    type="checkbox"
                    checked={v.is_enabled}
                    onChange={(e) => setDraft(row.operation, { is_enabled: e.target.checked })}
                  />
                </label>
              </div>
              {/* `notes` is the per-operation hint: what this operation
                  writes for the user. Seeded in migrations, editable by
                  an admin via PATCH. */}
              {row.notes !== null && row.notes !== "" && (
                <div style={LABEL}>{row.notes}</div>
              )}
              <div style={LABEL}>{t("llm_config.enabled_hint")}</div>
              <label>
                <span style={LABEL}>Model </span>
                <select
                  aria-label={`${row.operation} model`}
                  style={INPUT}
                  value={v.model}
                  onChange={(e) => {
                    const model = e.target.value;
                    const next = capsOf(model);
                    const fragment: Partial<LLMConfig> = { model };
                    // The API validates the row as it will look *after* the
                    // patch, so an effort the new model does not take has to
                    // go out cleared in the same body or the PATCH 422s.
                    const effort = v.reasoning_effort;
                    if (!next?.reasoning || (effort !== null && !next.efforts.includes(effort))) {
                      fragment.reasoning_effort = null;
                    }
                    // DUE-309: same cross-clear as `reasoning_effort` — a
                    // verbosity value the new model does not take would
                    // 422 the PATCH.
                    if (!next?.verbosity) {
                      fragment.verbosity = null;
                    }
                    setDraft(row.operation, fragment);
                  }}
                >
                  {catalogue.map((m) => (
                    <option key={m.name} value={m.name}>
                      {m.name}
                    </option>
                  ))}
                  {/* A model retired from the catalogue still has to render,
                      or the row would silently show someone else's model. */}
                  {caps === undefined && <option value={v.model}>{v.model}</option>}
                </select>
              </label>
              <label>
                <span style={LABEL}>max_tokens </span>
                <input
                  style={INPUT}
                  type="number"
                  value={v.max_tokens}
                  onChange={(e) =>
                    setDraft(row.operation, { max_tokens: Number(e.target.value) })
                  }
                />
              </label>
              <label>
                <span style={LABEL}>temperature </span>
                <input
                  style={INPUT}
                  type="number"
                  step="0.1"
                  placeholder={v.temperature === null ? "—" : undefined}
                  value={v.temperature ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setDraft(row.operation, {
                      temperature: raw === "" ? null : Number(raw),
                    });
                  }}
                />
              </label>
              {caps?.reasoning === true && (
                <>
                  <div style={LABEL}>{t("llm_config.temperature_ignored")}</div>
                  <label>
                    <span style={LABEL}>reasoning_effort </span>
                    <select
                      aria-label={`${row.operation} reasoning_effort`}
                      style={INPUT}
                      value={v.reasoning_effort ?? ""}
                      onChange={(e) =>
                        setDraft(row.operation, {
                          reasoning_effort: e.target.value === "" ? null : e.target.value,
                        })
                      }
                    >
                      <option value="">
                        {t("llm_config.effort_default", { effort: caps.default_effort })}
                      </option>
                      {caps.efforts.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              {caps?.verbosity === true && (
                <label>
                  <span style={LABEL}>verbosity </span>
                  <select
                    aria-label={`${row.operation} verbosity`}
                    style={INPUT}
                    value={v.verbosity ?? ""}
                    onChange={(e) =>
                      setDraft(row.operation, {
                        verbosity: e.target.value === "" ? null : e.target.value,
                      })
                    }
                  >
                    <option value="">{t("llm_config.verbosity_default")}</option>
                    {["low", "medium", "high"].map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div style={VALUE}>
                {t("llm_config.estimated_cost")}: ${estimateUSD(caps, v.max_tokens).toFixed(4)}
              </div>
              {dirty && (
                <div style={{ ...ROW, justifyContent: "flex-end" }}>
                  <button style={BTN} onClick={() => setPendingOp(row.operation)}>
                    {t("common.save")}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmModal
        open={pendingOp !== null}
        title={t("llm_config.confirm_title")}
        body={t("llm_config.confirm_body")}
        destructive
        onConfirm={() => pendingOp && save(pendingOp)}
        onClose={() => setPendingOp(null)}
      />
    </div>
  );
}
